import { useEffect } from "react";

/**
 * Injects JSON-LD structured data into <head> for AI search engines
 * (Google AI Overviews, Perplexity, ChatGPT, Bing Copilot).
 * Provides LocalBusiness, ItemList of products, and FAQPage schemas.
 */
export default function StructuredData() {
  useEffect(() => {
    const data = [
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://customade.in/#business",
        name: "Customade",
        alternateName: ["Customade Mumbai", "Customade Custom T-Shirt Store"],
        description:
          "Custom t-shirt and apparel manufacturer in Mumbai. Specialists in sublimation t-shirts, custom uniforms, polos, oversized tees, fleece hoodies, boiler suits, windcheaters and promotional merchandise. Bulk orders, embroidery, screen-printing and DTF printing.",
        url: "https://customade.in",
        telephone: "+918169272803",
        priceRange: "₹₹",
        image:
          "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/h4b1vodl_WhatsApp%20Image%202026-05-05%20at%2016.55.35.jpeg",
        logo:
          "https://customer-assets.emergentagent.com/job_landing-forge-60/artifacts/h4b1vodl_WhatsApp%20Image%202026-05-05%20at%2016.55.35.jpeg",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Gala No 5, Husseini Lakda Bazar, Nagpada, Madanpura",
          addressLocality: "Mumbai",
          addressRegion: "Maharashtra",
          postalCode: "400008",
          addressCountry: "IN",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: 18.964,
          longitude: 72.83,
        },
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: [
              "Monday",
              "Tuesday",
              "Wednesday",
              "Thursday",
              "Friday",
              "Saturday",
            ],
            opens: "10:30",
            closes: "19:30",
          },
        ],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.2",
          bestRating: "5",
          ratingCount: "30",
        },
        sameAs: [
          "https://maps.app.goo.gl/ss924pt1saDNCKKn8",
          "https://customade.in",
        ],
        areaServed: [
          { "@type": "City", name: "Mumbai" },
          { "@type": "AdministrativeArea", name: "Maharashtra" },
          { "@type": "Country", name: "India" },
        ],
        knowsAbout: [
          "Custom t-shirt printing",
          "Sublimation t-shirts",
          "Sports jerseys",
          "Cricket team jerseys",
          "Cotton round-neck t-shirts",
          "Cotton pique polos",
          "Oversized t-shirts",
          "Cotton fleece hoodies",
          "Industrial boiler suits",
          "Dangri coveralls",
          "Denim coveralls",
          "FR flame retardant coveralls",
          "Hi-Viz reflective coveralls",
          "Two-piece coveralls",
          "Safety jackets",
          "Reflective jackets",
          "Cargo trousers",
          "Windcheaters",
          "Corporate uniforms",
          "Embroidery",
          "Screen printing",
          "DTF printing",
          "Promotional merchandise",
        ],
        founder: { "@type": "Person", name: "Customade Team" },
      },
      {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: "Customade Product Catalogue",
        itemListElement: [
          {
            "@type": "Product",
            name: "Sublimation T-Shirts (Custom Sports Jerseys)",
            description:
              "All-over sublimation printed polyester t-shirts and cricket jerseys. 140–180 GSM polyester interlock / honeycomb fabric. Photo-sharp colour fidelity. Used by cricket teams, sports clubs, marathon organisers and fan merchandise drops in Mumbai and pan-India.",
            category: "Sports Apparel · Custom Printing",
            brand: { "@type": "Brand", name: "Customade" },
            url: "https://customade.in/sportswear",
          },
          {
            "@type": "Product",
            name: "Cotton Round-Neck T-Shirts (Bio-Washed Blanks)",
            description:
              "Regular-fit unisex round-neck t-shirts in 180 GSM bio-washed 100% cotton. Available in 22 shades — Black, White, Maroon, Navy Blue, Royal Blue, Sky Blue, Sapphire Blue, Mint Blue, Aqua, Sage Green, Olive Green, Coffee, Beige, Mustard, Golden Yellow, Lavender, Pearl Pink, Salmon Pink, Metallic Rose, Red, Steel Grey, Grey Melange. Sold blank for resale or fully customisable with print, embroidery or sublimation.",
            category: "Apparel · Cotton T-Shirts",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Cotton Pique Polos (220 GSM)",
            description:
              "Heavy 220 GSM cotton pique polos with contrast tipped collars. Available in 25 shades including Beige, Peach, Off White, Bisleri Green, Dark Gold, Baby Pink, Sky Blue, Lemon Yellow, Teal Blue, Special Orange, Raani Pink, Parrot Green, Lime Green, Orange, Sea Green, Red, Olive Green, Wine, Maroon, Grey Milange, Brown, Bottle Green, Navy and Black Anthra. Customisable with computerised embroidery, screen-print or sublimation. Worn by corporate teams including OPPO and ATHLS.",
            category: "Apparel · Polo Shirts",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Oversized T-Shirts (Drop Shoulder, 11 Shades)",
            description:
              "Premium 180 GSM oversized t-shirts with drop-shoulder fit and boxy hem. 11 modern shades — White, Lavender, Aqua, Sky Blue, Beige, Pearl Pink, Sage Green, Sapphire Blue, Steel Grey, Coffee, Black. Streetwear-grade combed cotton, ideal for clothing brands, college merchandise and fashion drops. Compatible with DTF, puff print and sublimation.",
            category: "Streetwear · Oversized Tees",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Cotton Fleece Hoodies",
            description:
              "Heavy 320 GSM brushed-back fleece hoodies in pure cotton (premium) or polycotton blend (budget). Drawstring hood, kangaroo front pocket, ribbed cuffs and waistband. Available in Jet Black, Cocoa Brown, Navy and other shades on request. Suitable for streetwear drops, college merch and brand launches.",
            category: "Outerwear · Hoodies",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Industrial Boiler Suits, Dangris & Coveralls",
            description:
              "Made-to-order single-piece coveralls (dangris) in 100% cotton drill, poly-cotton 65/35, denim or FR-treated cotton. 220–280 GSM. Sizes 36–50. Reinforced stress points, multiple utility pockets, brass YKK-style zip. Variants include Standard Cotton Dangri, Poly-Cotton Coverall, FR (Flame-Retardant) Coverall, Hi-Viz Reflective Coverall (EN ISO 20471), Two-Piece Coverall (Jacket + Pant) and Denim Coverall. Customisable with company-name embroidery, screen-printed logos, reflective tape and contrast yokes. MOQ 25 pieces. Used by factories, garages, oil rigs, welding shops, electrical crews, food-processing units and shipyards.",
            category: "Workwear · Industrial",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Cargo Trousers",
            description:
              "Utility cargo pants in cotton twill, ripstop cotton, poly-cotton or denim (240–320 GSM). Six-pocket construction with reinforced knees, drawstring or belt-loop waist, tapered or straight leg. Sizes 28–44 waist with custom inseams. Customisable with contrast pocket flaps, reflective tape, brand embroidery and custom rivets. MOQ 25 pieces. Ideal for site crews, event staff, adventure teams and streetwear brands.",
            category: "Workwear · Trousers",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Safety & Reflective Jackets (Hi-Viz)",
            description:
              "Fluorescent hi-viz safety jackets with EN ISO 20471-compliant 2\" 3M-grade silver reflective tape on chest, shoulders, sleeves and hem. Available in Fluorescent Orange, Fluorescent Yellow, Lime Green and Red. Styles: full-zip jacket, half-sleeve vest, mesh vest, waistcoat and bomber. Sizes S–5XL. MOQ 25 pieces. Made for road crews, construction sites, traffic teams, warehouse pickers and event marshals.",
            category: "Workwear · Safety",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Windcheaters & Custom Jackets",
            description:
              "Lightweight water-resistant windcheaters in micro-polyester, taffeta or NS-lined fabric. Hooded or hood-less, full-zip, elasticated cuffs. Reversible and fleece-lined variants available. Customisable with chest/back screen-print, sleeve embroidery, sublimated panels and custom zip pulls. Sizes S–5XL. MOQ 25 pieces. Ideal for monsoon merchandise, college fests, marathon kits and delivery teams.",
            category: "Outerwear · Windcheaters",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Custom Uniforms",
            description:
              "School, café, restaurant, hospital, retail and corporate uniforms — t-shirts, polos, shirts, aprons and overalls. Consistent batch dyeing, durable construction and logo placements that survive 50+ washes. Bulk-order specialists.",
            category: "Apparel · Uniforms",
            brand: { "@type": "Brand", name: "Customade" },
          },
          {
            "@type": "Product",
            name: "Promotional Merchandise",
            description:
              "Branded tote bags, caps, mugs, keychains, bottles and stationery for events, marketing campaigns and giveaways. Customisable with logos, tag-lines and full-colour prints.",
            category: "Promotional · Merch",
            brand: { "@type": "Brand", name: "Customade" },
          },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is the minimum order quantity (MOQ) for custom t-shirts in Mumbai?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Customade accepts orders starting from 25 pieces for sublimation t-shirts, polos, oversized tees and hoodies. For boiler suits and windcheaters the MOQ is also 25 pieces. Bulk pricing tiers begin at 50, 100, 250 and 500+ pieces.",
            },
          },
          {
            "@type": "Question",
            name: "How long does it take to deliver a custom t-shirt order?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Standard turnaround is 7–10 working days from artwork approval. Sublimation jerseys and embroidery polos take 8–12 days. Boiler suits and windcheaters take 12–18 days. Rush orders possible in 4–5 days at additional cost.",
            },
          },
          {
            "@type": "Question",
            name: "What printing techniques does Customade offer?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Sublimation, DTF (direct-to-film), screen printing, computerised embroidery, puff print and heat-transfer vinyl — all under one roof in Mumbai.",
            },
          },
          {
            "@type": "Question",
            name: "Where is Customade located?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Customade workshop is at Gala No 5, Husseini Lakda Bazar, Nagpada, Madanpura, Mumbai – 400008, Maharashtra, India. Open Monday–Saturday, 10:30 AM – 7:30 PM. WhatsApp +91 81692 72803.",
            },
          },
          {
            "@type": "Question",
            name: "Does Customade ship across India?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, Customade ships pan-India via Delhivery, BlueDart and DTDC. Mumbai metro: 24–48 hours after dispatch. International orders to UAE, UK, USA, Australia and Canada also accepted via DHL/FedEx.",
            },
          },
        ],
      },
    ];

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "customade-jsonld";
    script.text = JSON.stringify(data);
    // Remove old one if any (for hot reloads)
    const old = document.getElementById("customade-jsonld");
    if (old) old.remove();
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById("customade-jsonld");
      if (s) s.remove();
    };
  }, []);

  return null;
}
